import { gql } from "gql";
import { ProductOffer, ProductOffersContext } from "gql/graphql";
import { getActiveUser } from "@noom/hive-auth";
import { apolloClient, apolloClientLoggedOutToken } from "src/api/apolloClient";

export const PRODUCT_OFFERS_QUERY = gql(`
  query ProductOffers($input: ProductOffersInput!) {
    productOffers(input: $input) {
      offers {
        productName
        purchaseType
        orderOperation {
          ... on PurchaseOperation {
            saleItemId
            trialSaleItemId
            promoCode
          }
          ... on SwitchOperation {
            saleItemId
            trialSaleItemId
            promoCode
            subscriptionModificationReason
            shippingAddressId
            supplementaryInfo {
              multiUserInfos {
                additionalUserEmail
                additionalName
              }
            }
            switchType {
              ... on ImmediateSwitch {
                billingCycleBehavior
                prorationBehavior
                trialType
              }
              ... on NextBillingCycleSwitch {
                revertBehavior
              }
            }
          }
        }
        variants {
          orderOperation {
            ... on PurchaseOperation {
              saleItemId
              trialSaleItemId
              promoCode
            }
            ... on SwitchOperation {
              saleItemId
              trialSaleItemId
              promoCode
              subscriptionModificationReason
              shippingAddressId
              supplementaryInfo {
                multiUserInfos {
                  additionalUserEmail
                  additionalName
                }
              }
              switchType {
                ... on ImmediateSwitch {
                  billingCycleBehavior
                  prorationBehavior
                  trialType
                }
                ... on NextBillingCycleSwitch {
                  revertBehavior
                }
              }
            }
          }
        }
      }
      ineligibleOffers {
        productName
        purchaseType
        orderOperation {
          ... on PurchaseOperation {
            saleItemId
            trialSaleItemId
            promoCode
          }
          ... on SwitchOperation {
            saleItemId
            trialSaleItemId
            promoCode
            subscriptionModificationReason
            shippingAddressId
            supplementaryInfo {
              multiUserInfos {
                additionalUserEmail
                additionalName
              }
            }
            switchType {
              ... on ImmediateSwitch {
                billingCycleBehavior
                prorationBehavior
                trialType
              }
              ... on NextBillingCycleSwitch {
                revertBehavior
              }
            }
          }
        }
        variants {
          orderOperation {
            ... on PurchaseOperation {
              saleItemId
              trialSaleItemId
              promoCode
            }
            ... on SwitchOperation {
              saleItemId
              trialSaleItemId
              promoCode
              subscriptionModificationReason
              shippingAddressId
              supplementaryInfo {
                multiUserInfos {
                  additionalUserEmail
                  additionalName
                }
              }
              switchType {
                ... on ImmediateSwitch {
                  billingCycleBehavior
                  prorationBehavior
                  trialType
                }
                ... on NextBillingCycleSwitch {
                  revertBehavior
                }
              }
            }
          }
        }
      }
    }
  }
`);

export type ProductOffers = {
  offers: ProductOffer[];
  // A product name can appear in both lists (e.g. an eligible 5mg dose and an
  // ineligible 10mg dose).
  ineligibleOffers: ProductOffer[];
};

export async function getProductOffers(
  context?: ProductOffersContext
): Promise<ProductOffers> {
  // Search no longer eligibility-gates on accessCode (offers #810). Keep sending
  // it so promo rules can use purchase history (e.g. biomarkers first-purchase).
  const accessCode = getActiveUser()?.accessCode;
  // No user bearer when logged out, so use the anonymous client token.
  const client = accessCode ? apolloClient : apolloClientLoggedOutToken;

  const result = await client.query({
    query: PRODUCT_OFFERS_QUERY,
    variables: {
      input: { accessCode, context },
    },
    fetchPolicy: "no-cache",
  });

  const { offers, ineligibleOffers } = result.data.productOffers;
  return { offers, ineligibleOffers };
}
